'use client';

import { useTranslations } from 'next-intl';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { frontEndRoutes } from '@/constants/routes';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  translations: {
    title: string;
    description: string;
    tryAgain: string;
    goToSignIn: string;
  };
};

type State = {
  hasError: boolean;
  error?: Error;
};

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to monitoring service if needed
    if (process.env.NODE_ENV === 'development') {
      console.error('Auth Error Boundary caught:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    // Force refresh the page
    window.location.reload();
  };

  handleNavigateToSignIn = () => {
    window.location.href = frontEndRoutes.auth.signIn;
  };

  render() {
    if (this.state.hasError === true) {
      if (this.props.fallback !== undefined && this.props.fallback !== null) {
        return this.props.fallback;
      }

      const { translations } = this.props;

      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center space-y-1">
              <CardTitle className="text-2xl font-semibold tracking-tight text-error">
                {translations.title}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {translations.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {process.env.NODE_ENV === 'development' && this.state.error !== undefined && (
                <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  <code>{this.state.error.message}</code>
                </div>
              )}
              <div className="grid gap-2">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={this.handleReset}
                >
                  {translations.tryAgain}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={this.handleNavigateToSignIn}
                >
                  {translations.goToSignIn}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component that provides translations to AuthErrorBoundary
 */
export function AuthErrorBoundaryWithTranslations({ children, fallback }: Omit<Props, 'translations'>) {
  const translate = useTranslations();

  const translations = {
    title: translate('auth.errorBoundary.title'),
    description: translate('auth.errorBoundary.description'),
    tryAgain: translate('auth.errorBoundary.tryAgain'),
    goToSignIn: translate('auth.errorBoundary.goToSignIn'),
  };

  return (
    <AuthErrorBoundary translations={translations} fallback={fallback}>
      {children}
    </AuthErrorBoundary>
  );
}
